import React, { useEffect, useRef, useState } from "react"
import BLUR_MAP from "../../data/hotelBlurMap.json"

// Respiration immersive plein écran façon pages produit Apple :
// image full-bleed avec parallaxe + zoom doux pilotés au scroll (rAF, passif),
// grande phrase typographique révélée à l'entrée dans le viewport.
// SSR-safe (tout accès window en useEffect) ; prefers-reduced-motion respecté.
// `imgMobile` est optionnel : quand une respiration a un cadrage portrait dédié,
// il est servi sous 768px via <picture> (un seul fichier téléchargé, jamais les deux).
// Sans lui, le comportement est inchangé — `img` sert partout.
const ImmersiveBreak = ({ img, imgMobile, alt, eyebrow, title, sub, align = "center" }) => {
  const sectionRef = useRef(null)
  const imgRef = useRef(null)
  const textRef = useRef(null)
  const [loaded, setLoaded] = useState(false)
  const blur = BLUR_MAP[img]

  useEffect(() => {
    // image déjà en cache : onLoad ne refire pas
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) setLoaded(true)
  }, [])

  useEffect(() => {
    const section = sectionRef.current
    const image = imgRef.current
    const text = textRef.current
    if (!section || !image || !text) return

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    // Révélation du texte
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            text.style.opacity = "1"
            text.style.transform = "translateY(0)"
            io.unobserve(text)
          }
        })
      },
      { threshold: 0.35 }
    )
    io.observe(text)

    if (reduced) {
      image.style.transform = "scale(1.06)"
      return () => io.disconnect()
    }

    // Parallaxe + zoom au scroll
    let raf = 0
    let visible = false
    const update = () => {
      raf = 0
      const r = section.getBoundingClientRect()
      const vh = window.innerHeight || 1
      // progression -1 (section sous le viewport) → +1 (au-dessus)
      const p = Math.max(-1, Math.min(1, (vh / 2 - (r.top + r.height / 2)) / vh))
      image.style.transform = `translate3d(0, ${(p * 5).toFixed(2)}%, 0) scale(${(1.14 - Math.abs(p) * 0.05).toFixed(3)})`
    }
    const onScroll = () => { if (visible && !raf) raf = requestAnimationFrame(update) }
    const vis = new IntersectionObserver(e => { visible = e[0].isIntersecting; if (visible) onScroll() }, { rootMargin: "20% 0px" })
    vis.observe(section)
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })
    update()
    return () => {
      io.disconnect(); vis.disconnect()
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  const alignStyles = align === "left"
    ? { alignItems:"flex-end", textAlign:"left", justifyContent:"flex-start" }
    : { alignItems:"center", textAlign:"center", justifyContent:"center" }

  return (
    <section
      ref={sectionRef}
      style={{ position:"relative", height:"100svh", minHeight:560, maxHeight:1100, overflow:"hidden", background:"#050505" }}
    >
      {blur && (
        <div
          aria-hidden="true"
          style={{ position:"absolute", inset:0, backgroundImage:`url(${blur})`, backgroundSize:"cover", backgroundPosition:"center", transform:"scale(1.2)", filter:"blur(24px) saturate(1.1)" }}
        />
      )}
      {/* display:contents — <picture> est le seul enfant en flux de la section :
          on lui retire toute boîte pour ne rien changer aux 57 pages qui
          utilisent déjà cette respiration. */}
      <picture style={{ display: "contents" }}>
        {imgMobile && <source media="(max-width: 767px)" srcSet={imgMobile} />}
        <img
          ref={imgRef}
          src={img}
          alt={alt}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", transform:"scale(1.14)", willChange:"transform", opacity: !blur || loaded ? 1 : 0, transition:"opacity 700ms cubic-bezier(0.22,1,0.36,1)" }}
        />
      </picture>
      <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.55) 100%)" }} />
      <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", padding:"clamp(28px,6vw,80px)", ...alignStyles }}>
        <div
          ref={textRef}
          style={{ maxWidth:820, opacity:0, transform:"translateY(28px)", transition:"opacity 900ms cubic-bezier(0.22,1,0.36,1), transform 900ms cubic-bezier(0.22,1,0.36,1)", padding:"48px 56px", margin:"-48px -56px", background:"radial-gradient(ellipse at center, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.12) 55%, transparent 78%)", borderRadius:32 }}
        >
          {eyebrow && (
            <div style={{ fontSize:12, letterSpacing:"0.24em", textTransform:"uppercase", color:"rgba(255,255,255,0.75)", marginBottom:16 }}>{eyebrow}</div>
          )}
          <div style={{ fontSize:"clamp(32px,5.6vw,76px)", fontWeight:600, letterSpacing:"-0.045em", lineHeight:1.05, color:"#fff", textShadow:"0 2px 28px rgba(0,0,0,0.45)" }} dangerouslySetInnerHTML={{ __html: title }} />
          {sub && (
            <div style={{ fontSize:"clamp(15px,1.5vw,20px)", color:"rgba(255,255,255,0.85)", marginTop:18, lineHeight:1.5, textShadow:"0 1px 12px rgba(0,0,0,0.4)" }}>{sub}</div>
          )}
        </div>
      </div>
    </section>
  )
}

export default ImmersiveBreak
